import { type FunctionComponent, useState } from 'react';

import { captureException } from '@sentry/react';

import Avatar from 'components/Avatar';

import { logout } from 'services/auth';
import {
  P_ADMIN_ADMIN_USERS,
  P_ADMIN_BLOG,
  P_ADMIN_CONTENT_MANAGEMENT,
  P_ADMIN_PRACTITIONERS,
  P_ADMIN_PROFILE,
  P_ADMIN_RESEARCH_LIB,
  P_ADMIN_USERS,
  useGlobalState,
} from 'utils';

import IconHamburger from 'assets/images/ai/icon-hamburger.svg';
import IconLogout from 'assets/images/ai/icon-logout.svg';
import IconSidebar from 'assets/images/ai/icon-sidebar.svg';
import IconAdminUser from 'assets/images/icon-admin-user.svg';
import IconBlogEditor from 'assets/images/icon-blog-editor.svg';
import IconContentManagement from 'assets/images/icon-content-management.svg';
import IconDashboard from 'assets/images/icon-dashboard.svg';
import IconMyProfile from 'assets/images/icon-my-profile.svg';
import IconPractitioner from 'assets/images/icon-practitioner.svg';
import IconResearchLib from 'assets/images/icon-research-lib.svg';

import Logo from 'assets/images/logo-soul-sanctuary-new-60px.svg';

import Item from './Item';

import './style.scss';

const CmsMenu: FunctionComponent = () => {
  const [user] = useGlobalState('user');
  if (!user) throw Error('Internal error');

  const [isExpandedRaw, setIsExpandedRaw] = useGlobalState('ui.adminSidebarExpanded');
  const isExpanded = isExpandedRaw !== false;

  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const userRoles: string[] = [];
  if (user.roles.includes('OWNER')) userRoles.push('Admin');
  if (user.roles.includes('ARTIST')) userRoles.push('Practitioner');
  if (!userRoles.length) userRoles.push('Registered User');

  const closeMobileMenu = () => {
    setIsMobileOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      location.href = '/';
    } catch (error) {
      captureException(error);
      // eslint-disable-next-line no-alert
      alert('Failed to logout');
    }
  };

  return (
    <>
      <button
        aria-label="Toggle menu"
        onClick={() => {
          setIsMobileOpen(!isMobileOpen);
        }}
        styleName="mobileToggle"
        type="button"
      >
        <IconHamburger />
      </button>
      <div
        styleName={`sidebarWrapper ${isExpanded ? 'expanded' : ''} ${isMobileOpen ? 'mobileOpen' : ''}`}
      >
        <div styleName="sidebarContainer">
          <div styleName={`topSection ${isExpanded ? 'expanded' : ''}`}>
            <div styleName="logoWrapper">
              <Logo styleName="logo" />
            </div>
            <button
              aria-label="Collapse menu"
              onClick={() => {
                setIsExpandedRaw(!isExpanded);
              }}
              styleName={`toggleButton ${isExpanded ? 'expanded' : ''}`}
              type="button"
            >
              <IconSidebar />
            </button>
          </div>
          <div styleName="menuItems">
            <Item
              end
              expanded={isExpanded}
              icon={<IconDashboard />}
              name="Dashboard"
              onClick={closeMobileMenu}
              to="/"
            />
            {user.permissions.EDIT_ANY_VIDEO_DETAILS
              || user.permissions.VIDEO_UPLOAD ? (
                <Item
                  expanded={isExpanded}
                  icon={<IconContentManagement />}
                  name="Content Management"
                  onClick={closeMobileMenu}
                  to={P_ADMIN_CONTENT_MANAGEMENT}
                />
              ) : null}
            {user.permissions.BLOG_EDITOR ? (
              <>
                <Item
                  expanded={isExpanded}
                  icon={<IconBlogEditor />}
                  name="Blog Editor"
                  onClick={closeMobileMenu}
                  to={P_ADMIN_BLOG}
                />
                <Item
                  expanded={isExpanded}
                  icon={<IconResearchLib />}
                  name="Research Lib"
                  onClick={closeMobileMenu}
                  to={P_ADMIN_RESEARCH_LIB}
                />
              </>
            ) : null}
            {user.permissions.USER_MANAGEMENT ? (
              <Item
                expanded={isExpanded}
                icon={<IconPractitioner />}
                name="Practitioners"
                onClick={closeMobileMenu}
                to={P_ADMIN_PRACTITIONERS}
              />
            ) : null}
            {user.permissions.USER_MANAGEMENT ? (
              <Item
                expanded={isExpanded}
                icon={<IconAdminUser />}
                name="Admin Users"
                onClick={closeMobileMenu}
                to={P_ADMIN_ADMIN_USERS}
              />
            ) : null}
            {user.permissions.USER_MANAGEMENT ? (
              <Item
                expanded={isExpanded}
                icon={<IconAdminUser />}
                name="User Management"
                onClick={closeMobileMenu}
                to={P_ADMIN_USERS}
              />
            ) : null}
            <Item
              expanded={isExpanded}
              icon={<IconMyProfile />}
              name="My Profile"
              onClick={closeMobileMenu}
              to={P_ADMIN_PROFILE}
            />
          </div>
          <div styleName={`profileSection ${isExpanded ? 'expanded' : ''}`}>
            <Avatar user={user} />
            <div styleName="nameAndRole">
              <div styleName="name">{user.firstName} {user.lastName}</div>
              <div styleName="role">{userRoles.join(', ')}</div>
            </div>
          </div>
          <div styleName={`bottomSection ${isExpanded ? 'expanded' : ''}`}>
            <button
              onClick={() => {
                void handleLogout();
              }}
              styleName={`logoutButton ${isExpanded ? 'expanded' : ''}`}
              type="button"
            >
              <span styleName="icon"><IconLogout /></span>
              <span styleName="label">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CmsMenu;
